<template>
    <div class="order-info">
        <div class="static-data">
            <b-card no-body class="static-data--card">
                <b-card-header :class="['static-data--card-header', staticOrderInfo.isOrderReferenceFinished ? 'finished-background' : 'in-production-background']">
                    <div class="title">Orden: <span class="span-with-data">{{ staticOrderInfo.name }}</span> - Lote: <span class="span-with-data">{{ staticOrderInfo.batch }}</span></div>
                    <div class="production-line-name">{{ staticOrderInfo.production_line_name }}</div>
                </b-card-header>
                <b-card-body class="static-data--card-body">
                    <div class="client">
                        <div class="client--value fw-title-boldest">{{ staticOrderInfo.client }}</div>
                        <div class="client--text">Cliente</div>
                    </div>
                    <div class="reference">
                        <div class="reference--value fw-title-boldest">{{ staticOrderInfo.reference }}</div>
                        <div class="reference--text">Artículo</div>
                    </div>
                    <div class="start-date">
                        <div class="start-date--value fw-title-boldest">{{ staticOrderInfo.startDate }}</div>
                        <div class="start-date--text">Fecha de inicio</div>
                    </div>
                    <div class="real-end-date" v-if="dynamicOrderInfo.isFinished">
                        <div class="real-end-date--value fw-title-boldest">{{ staticOrderInfo.realEndDate }}</div>
                        <div class="real-end-date--text">Fecha de fin</div>
                    </div>
                    <div class="planned-end-date" v-else>
                        <div class="planned-end-date--value fw-title-boldest">{{ staticOrderInfo.idealEndDate }}</div>
                        <div class="planned-end-date--text">Fecha de fin prevista</div>
                    </div>
                    <div class="worked-time">
                        <div class="worked-time--value fw-title-boldest">{{ staticOrderInfo.workedTime }}</div>
                        <div class="worked-time--text">Tiempo total trabajado</div>
                    </div>
                    <div class="gross-weight">
                        <div class="gross-weight--value fw-title-boldest">{{ staticOrderInfo.grossWeight }}</div>
                        <div class="gross-weight--text">Peso bruto</div>
                    </div>
                </b-card-body>
            </b-card>
        </div>
        <div class="dynamic-data">
            <div class="completed-percentage">
                <div class="completed-percentage--text">
                    <div class="completed-percentage--values">
                        <div class="completed-percentage--amount">
                            <div class="amount">{{ dynamicOrderInfo.completedPercentage }}</div>
                            <div class="symbol">%</div>
                        </div>
                        <span>Completado</span>
                    </div>
                    <div class="completed-quantity"  :class="amountClasses">
                        <div class="completed-quantity--values">
                            <span class="actual-quantity">{{ dynamicOrderInfo.okQuantity }}</span>
                            <div class="separator">/</div>
                            <span class="objective-quantity">{{ dynamicOrderInfo.objectiveQuantity }}</span>
                        </div>
                        <div class="format">{{ staticOrderInfo.productionLineDefaultPackagingFormat }}</div>
                    </div>
                </div>
                <div class="completed-percentage--bar-wrapper">
                    <PercentageBar
                        v-for="(completedPercentageBar, index) in dynamicOrderInfo.completedPercentageBars"
                        :key="index"
                        :producedQuantity="completedPercentageBar.produced_quantity"
                        :barPercentageMax="dynamicOrderInfo.barPercentageMax"
                        :percentageBarStartDate="completedPercentageBar.percentage_bar_start_date"
                        :percentageBarEndDate="completedPercentageBar.percentage_bar_end_date"
                        :shiftDescrip="completedPercentageBar.shift_descrip"
                        :bagsProduced="completedPercentageBar.bags_produced"
                        :bagsToProduce="completedPercentageBar.bags_to_produce"
                        :barStyle="completedPercentageBarStyle(completedPercentageBar.produced_quantity, dynamicOrderInfo.barPercentageMax, index)"
                    />
                </div>
            </div>
            <div class="dynamic-data--data-squares">
                <div class="delayed-quantity data-square">
                    <div class="delayed-quantity--text data-square--text">
                        <div class="text--title">
                            <div class="packaging-format">{{ staticOrderInfo.productionLineDefaultPackagingFormat }}</div>
                            <div class="text">atrasadas</div>
                        </div>
                        <div class="text--subtitle">Fecha de finalización óptima</div>
                    </div>
                    <div class="delayed-quantity--data data-square--data">
                        <div class="data--main">{{ dynamicOrderInfo.delayedQuantity }}</div>
                        <div class="data--secondary">{{ dynamicOrderInfo.theoreticalEndDate }}</div>
                    </div>
                </div>
                <div :class="' quality data-square ' + qualityClass">
                    <div class="quality--text data-square--text box--center">
                        <div class="text--title">Calidad</div>
                    </div>
                    <div class="quality--data data-square--data box--center">
                        <div class="data--main">{{ dynamicOrderInfo.quality }}%</div>
                    </div>
                </div>
                <div class="lost-time data-square">
                    <div class="lost-time--text data-square--text">
                        <div class="text--title">Tiempo de atraso</div>
                        <div class="text--subtitle">Porcentaje de atraso</div>
                    </div>
                    <div class="lost-time--data data-square--data">
                        <div class="data--main">{{ dynamicOrderInfo.lostTime }}</div>
                        <div class="data--secondary">{{ dynamicOrderInfo.lostPercentage }}%</div>
                    </div>
                </div>
                <div class="production-coefficient data-square">
                    <div class="production-coefficient--text data-square--text">
                        <div class="text--title">Cadencia productiva</div>
                        <div class="text--subtitle">Real vs teórica</div>
                    </div>
                    <div class="production-coefficient--data data-square--data">
                        <div class="data--main">{{ dynamicOrderInfo.productionCoefficient }}/{{ dynamicOrderInfo.theoreticalProductionCoefficient }} </div>
                        <div class="data--secondary">{{ staticOrderInfo.productionLineDefaultPackagingFormat }}/Hora</div>
                    </div>
                </div>
                <div class="accumulated-weight data-square">
                    <div class="accumulated-weight--text data-square--text">
                        <div class="text--title">Peso acumulado</div>
                        <div class="text--subtitle">Valor en cajas</div>
                    </div>
                    <div class="accumulated-weight--data data-square--data">
                        <div class="data--main">{{ dynamicOrderInfo.accumulatedWeight }}<span style="font-size: 1.1rem;"> KG</span></div>
                        <div class="data--secondary">{{ dynamicOrderInfo.accumulatedWeightInBoxes }}<span style="font-size: 0.8rem;"> Cajas</span></div>
                    </div>
                </div>
                <div class="accumulated-weight data-square">
                    <div class="accumulated-weight--text data-square--text">
                        <div class="text--title">Peso en exceso</div>
                        <div class="text--subtitle">Valor en cajas</div>
                    </div>
                    <div class="accumulated-weight--data data-square--data">
                        <div class="data--main">{{ dynamicOrderInfo.excessWeight }}<span style="font-size: 1.1rem;"> KG</span></div>
                        <div class="data--secondary">{{ dynamicOrderInfo.excessWeightInBoxes }}<span style="font-size: 0.8rem;"> Cajas</span></div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import PercentageBar from './PercentageBar.vue';
export default {
    name: "OrderInfo",
    components:{
        PercentageBar
    },
    props:{
        staticOrderInfo: Object,
        dynamicOrderInfo: Object,
    },
    methods:{
        completedPercentageBarStyle(producedQuantity, objectiveQuantity, index) {
            let width;
            if (!producedQuantity || !objectiveQuantity) {
                width = `width: 0%`;
            } else if (producedQuantity > objectiveQuantity) {
                width = `width: 100%`;
            } else {
                width = `width: ${producedQuantity * 100 / objectiveQuantity}%`;
            }

            let bgColor = (index % 2 === 0) ? 'background-color: #ffc107' : 'background-color: #ffdf80';
            let border = '';
            if(producedQuantity == 0) {
                border = 'border: 1px solid lightgray';
            }
            return `${width}; ${bgColor}; ${border};`;
        },
    },
    computed:{
        qualityClass(){
            let qualityClass = 'good-quality';
            if(this.dynamicOrderInfo.quality < 75){
                qualityClass = 'bad-quality';
            }else if(this.dynamicOrderInfo.quality < 85){
                qualityClass = 'warning-quality';
            }
            return qualityClass;
        },
        hasDangerQuantity(){
            return this.dynamicOrderInfo.okQuantity > this.dynamicOrderInfo.objectiveQuantity;
        },
        isProducing(){
            return !this.staticOrderInfo.isOrderReferenceFinished;
        },
        amountClasses(){
            let classes = [];
            if(this.hasDangerQuantity){
                classes.push('danger-quantity');
                if(this.isProducing){
                    classes.push('danger-quantity-blink');
                }
            }
            return classes;
        }
    }
}
</script>

<style scoped lang="scss">
    .span-with-data{
        font-weight: bold;
    }
    .finished-background{
        background-color: #27884F;
        border: 1px solid #27884F;
        color: white;
    }
    .in-production-background{
        background-color: #355fd4 ;
        border: 1px solid #355fd4;
        color: white;
    }
    .dynamic-data{
        .dynamic-data--data-squares{
            .data-square {
                border: 1px solid #bebebe;
                border-radius: 5px;            
            }
        }
        .completed-quantity{
            font-size: 1.15rem;
        }
    }
</style>